// src/pages/customerApp/TimelineActivities.tsx
import React, { useEffect, useState } from 'react';
import {
  Card, Typography, Row, Col, Space, Button, Spin, Empty, Avatar, Tag, Divider
} from 'antd';
import {
  ArrowLeftOutlined, ThunderboltOutlined, MessageOutlined, RocketOutlined,
  ClockCircleOutlined, CalendarOutlined, UserOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './customerHome.css';
import { useAuth } from '../../context/AuthContext';
import { listActivitiesByCustomer, type ActivityDTO } from '../../services/activities';

const { Title, Text, Paragraph } = Typography;

const timeAgo = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const h = 60 * 60 * 1000, dms = 24 * h;
  if (diff < h) return 'Ora';
  if (diff < dms) return `${Math.floor(diff / h)}h`;
  const days = Math.floor(diff / dms);
  if (days === 1) return 'Ieri';
  if (days < 7) return `${days}g`;
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
};

const formatFull = (iso: string) =>
  new Date(iso).toLocaleString('it-IT', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

const iconFor = (a: ActivityDTO) => {
  const code = a.activityType?.code || (a as any).type || '';
  const label = a.activityType?.label || '';
  if (code.includes('POST') || label.includes('POST')) return <MessageOutlined />;
  if (code.includes('ADS') || label.includes('ADS')) return <RocketOutlined />;
  if (code.includes('IG')) return <ThunderboltOutlined />;
  return <ThunderboltOutlined />;
};

const colorFor = (a: ActivityDTO) => {
  const code = a.activityType?.code || (a as any).type || '';
  const label = a.activityType?.label || '';
  if (code.includes('POST') || label.includes('POST')) return '#6366f1';
  if (code.includes('ADS') || label.includes('ADS')) return '#ec4899';
  if (code.includes('IG')) return '#8b5cf6';
  return '#06b6d4';
};

const getWhen = (a: ActivityDTO) =>
  (a as any).createdAt || (a as any).occurredAt || new Date().toISOString();

const TimelineActivities: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<ActivityDTO[]>([]);

  useEffect(() => {
    const load = async () => {
      if (!user?.customerId) return;
      setLoading(true);
      try {
        const r = await listActivitiesByCustomer(user.customerId, 0, 50);
        setActivities(r.content ?? []);
      } catch {
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.customerId]);

  return (
    <div className="customer-home-v2 page-transition" style={{ paddingTop: 16 }}>
      <div className="container-v2">
        {/* Header stile identico */}
        <div className="header-v2" style={{ marginBottom: 24 }}>
          <div className="header-content-v2">
            <div className="user-info">
              <div className="user-details">
                <Text className="welcome-label">Attività</Text>
                <Title level={1} className="user-name">Timeline</Title>
              </div>
            </div>
            <Button
              type="default"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              className="logout-button-v2"
            >
              Indietro
            </Button>
          </div>
        </div>

        {/* Lista card con stesso look delle activity cards della Home */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text style={{ color: '#6b7280' }}>Caricamento attività...</Text>
            </div>
          </div>
        ) : activities.length === 0 ? (
          <Card className="empty-card" bordered={false}>
            <Empty description="Nessuna attività trovata" />
          </Card>
        ) : (
          <Row gutter={[16, 16]}>
            {activities.map((a) => {
              const when = getWhen(a);
              const color = colorFor(a);
              return (
                <Col xs={24} md={12} key={a.id}>
                  <Card className="activity-card-v2" hoverable bordered={false}>
                    <div className="activity-card-inner">
                      <div className="activity-header-v2">
                        <Avatar
                          icon={iconFor(a)}
                          size={48}
                          style={{ background: color }}
                          className="activity-avatar-v2"
                        />
                        <Text className="activity-time-v2">{timeAgo(when)}</Text>
                      </div>

                      <Title level={5} className="activity-title-v2">
                        {a.name?.length > 60 ? `${a.name.slice(0, 60)}…` : a.name}
                      </Title>

                     

                      {a.description && (
                        <Paragraph style={{ color: '#6b7280', margin: '6px 0 0 0' }} ellipsis={{ rows: 2 }}>
                          {a.description}
                        </Paragraph>
                      )}

                      <Divider style={{ margin: '10px 0', borderColor: 'rgba(0,0,0,0.06)' }} />

                      <Row gutter={[12, 8]}>
                        
                        <Col>
                          <Space size={6}>
                            <CalendarOutlined style={{ color: '#9ca3af', fontSize: 13 }} />
                            <Text style={{ color: '#6b7280', fontSize: 13 }}>{formatFull(when)}</Text>
                          </Space>
                        </Col>
                        
                      </Row>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>
    </div>
  );
};

export default TimelineActivities;
